import { api, ApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import type { CadSysteemEnum } from '@/api/cadJson';
import type { OdooTask, ProjectDetail, MateriaalLine, StockPicking, StockMove, Collector, Kring, PhotoItem, SharePointItem, FaseSysteem } from '@/types/odoo';
import type { Meerwerk } from '@/api/meerwerken';

export async function fetchTasks(date: string): Promise<{
  tasks: OdooTask[];
  installerNames: Record<number, string>;
  projectNames: Record<number, string>;
  werfAddresses: Record<number, string>;
  photoStats?: Record<number, { uploaded: number; total: number }>;
  fasePhotoStats?: Record<number, { uploaded: number; total: number }>;
  faseSystems?: Record<number, FaseSysteem>;
}> {
  return api<{
    tasks: OdooTask[];
    installerNames: Record<number, string>;
    projectNames: Record<number, string>;
    werfAddresses: Record<number, string>;
    photoStats?: Record<number, { uploaded: number; total: number }>;
    fasePhotoStats?: Record<number, { uploaded: number; total: number }>;
    faseSystems?: Record<number, FaseSysteem>;
  }>(`/api/tasks?date=${date}`);
}

export async function fetchProject(projectId: number): Promise<ProjectDetail> {
  return api<ProjectDetail>(`/api/projects/${projectId}`);
}

export async function fetchMateriaallijst(projectId: number): Promise<{ lines: MateriaalLine[]; pickings: StockPicking[]; moves: StockMove[] }> {
  return api<{ lines: MateriaalLine[]; pickings: StockPicking[]; moves: StockMove[] }>(`/api/projects/${projectId}/materiaallijst`);
}

export interface CollectorPicking {
  id: number;
  name: string;
  fase_name: string | null;
  label: string;
  carrier_tracking_ref: string | null;
  /** ISO 8601 (UTC) creation time of the picking = upload moment of the lijst. */
  uploaded_at: string | null;
}

export async function fetchCollectoren(projectId: number): Promise<{ collectors: Collector[]; installerNames: Record<string, string>; allInstallerNames: Record<string, string>; pickings: CollectorPicking[] }> {
  return api<{ collectors: Collector[]; installerNames: Record<string, string>; allInstallerNames: Record<string, string>; pickings: CollectorPicking[] }>(`/api/projects/${projectId}/collectoren`);
}

const RETRYABLE_HTTP_STATUSES = new Set([502, 503, 504]);
const RETRY_DELAYS_MS = [1000, 4000];

function jitter(ms: number): number {
  return Math.round(ms * (0.75 + Math.random() * 0.5));
}

function isRetryableUploadError(err: unknown): boolean {
  if (err instanceof ApiError) {
    if (err.code === 'NETWORK_ERROR' || err.status === 0) return true;
    if (RETRYABLE_HTTP_STATUSES.has(err.status)) return true;
    return false;
  }
  // Fallback: api() wraps fetch failures, but tolerate raw network errors too
  if (err instanceof TypeError) return true;
  if (err instanceof Error && err.name === 'AbortError') return true;
  return false;
}

function buildCollectorUploadFormData(
  file: File,
  meta: { projectName: string; blok: string; verdieping: string; collectorName: string; taskDateDeadline?: string },
): FormData {
  const formData = new FormData();
  // Fields MUST come before file for @fastify/multipart to parse them
  formData.append('projectName', meta.projectName);
  formData.append('blok', meta.blok);
  formData.append('verdieping', meta.verdieping);
  formData.append('collectorName', meta.collectorName);
  if (meta.taskDateDeadline) formData.append('taskDateDeadline', meta.taskDateDeadline);
  formData.append('file', file);
  return formData;
}

export async function uploadCollectorPhoto(
  collectorId: number,
  file: File,
  meta: { projectName: string; blok: string; verdieping: string; collectorName: string; taskDateDeadline?: string },
): Promise<{ url: string; key: string; opDrukChanged?: boolean }> {
  const maxAttempts = 3;
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const formData = buildCollectorUploadFormData(file, meta);
    try {
      return await api<{ url: string; key: string; opDrukChanged?: boolean }>(
        `/api/photos/collector/${collectorId}`,
        { method: 'POST', body: formData },
      );
    } catch (err) {
      lastError = err;
      if (attempt === maxAttempts || !isRetryableUploadError(err)) {
        throw err;
      }
      const detail =
        err instanceof ApiError
          ? `${err.code} (status ${err.status})`
          : err instanceof Error
            ? err.name
            : 'unknown';
      const delay = jitter(RETRY_DELAYS_MS[attempt - 1] ?? 4000);
      console.warn(
        `[uploadCollectorPhoto] attempt ${attempt}/${maxAttempts} failed: ${detail}. Retrying in ${delay}ms...`,
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw lastError;
}

export async function listCollectorPhotos(
  collectorId: number,
  meta: { projectName: string; blok: string; verdieping: string; collectorName: string },
): Promise<{ photos: PhotoItem[]; total: number; fotoStatus: string | null }> {
  const params = new URLSearchParams(meta);
  return api<{ photos: PhotoItem[]; total: number; fotoStatus: string | null }>(`/api/photos/collector/${collectorId}?${params}`);
}

export async function listProjectPhotoBatch(projectName: string): Promise<Record<string, PhotoItem[]>> {
  const params = new URLSearchParams({ projectName });
  return api<Record<string, PhotoItem[]>>(`/api/photos/project-batch?${params}`);
}

export async function deleteCollectorPhoto(collectorId: number, key: string): Promise<{ opDrukChanged?: boolean }> {
  return api<{ opDrukChanged?: boolean }>(`/api/photos/collector/${collectorId}?key=${encodeURIComponent(key)}`, { method: 'DELETE' });
}

// Foto-controle "whose queue" dropdown. projectleider = an Odoo user id (view
// that PL's queue), the literal 'all' (every PL's queue), or undefined (own queue).
export interface FotoReviewProjectleider {
  id: number;
  name: string;
}

export async function listFotoReviewProjectleiders(): Promise<FotoReviewProjectleider[]> {
  const { projectleiders } = await api<{ projectleiders: FotoReviewProjectleider[] }>(
    '/api/photos/review/projectleiders',
  );
  return projectleiders;
}

function fotoReviewQuery(projectleider?: string): string {
  return projectleider ? `?projectleider=${encodeURIComponent(projectleider)}` : '';
}

export async function listFotoReview<T>(projectleider?: string): Promise<T> {
  return api<T>(`/api/photos/review${fotoReviewQuery(projectleider)}`);
}

export async function getFotoReviewProject<T>(projectId: number, projectleider?: string): Promise<T> {
  return api<T>(`/api/photos/review/${projectId}${fotoReviewQuery(projectleider)}`);
}

export async function updatePhotoStatus(collectorId: number, status: string): Promise<void> {
  await api(`/api/photos/collector/${collectorId}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
}

/**
 * A meerwerk surfaced by the foto-controle review endpoint. Same as a Meerwerk
 * record but enriched with whether the submitter is an external subcontractor.
 * isSubcontractor === false → own Thermoduct worker; no PO allowed.
 */
export interface ReviewMeerwerk extends Meerwerk {
  isSubcontractor: boolean;
}

/** A post-money follow-up that failed (e.g. the meerwerk status-flip after its PO line landed).
 *  The PO line IS billed; the source record wasn't updated. Surfacing this prevents a
 *  reported-success that actually left the record re-billable. */
export interface AddToPoPartialFailure {
  kind: 'meerwerk_flip' | 'manual_create';
  meerwerkId?: number;
  name: string;
  action: string;
  state: string;
  fix: string;
}

/** Result of billing onto the rolling PO (200, or 207 with warnings/partialFailures). */
export interface AddToPoResult {
  /** Present on 207 responses when non-fatal issues occurred (collector flips). */
  warnings?: string[];
  /** Present on 207 when a post-line write failed (e.g. meerwerk status-flip) — the line is
   *  billed but the record stayed pending/unlinked, so it must NOT be reported as a clean success. */
  partialFailures?: AddToPoPartialFailure[];
  [key: string]: unknown;
}

/** Optional pricing levers for billing (mirrors the onderaannemers tab). */
export interface AddToPoOptions {
  /** When true, also bills the "Collector geplaatst" placement line. */
  geplaatst?: boolean;
  /** Override the auto-computed supplier tier (0-200 vs 200+). */
  tier?: 'under200' | 'from200';
  /**
   * When true, the reviewer explicitly acknowledges that the collector's fase may have
   * been client-placed ("Collectoren geleverd"). Required by the server when the
   * collector is authoritatively flagged and geplaatst is not set.
   */
  acknowledgedGeleverd?: boolean;
}

/**
 * Bill a collector onto the installer's rolling PO and flip its foto status to
 * Fotos_Goedgekeurd. Returns warnings on a 207 partial-success.
 *
 * Throws ApiError with:
 *   - code === 'COLLECTOR_ALREADY_ON_PO', body.conflicts for 409
 *   - code === 'NO_INSTALLATEUR' for 400
 */
export async function addCollectorToPo(collectorId: number, opts: AddToPoOptions = {}): Promise<AddToPoResult> {
  return api<AddToPoResult>(`/api/photos/collector/${collectorId}/add-to-po`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  });
}

export interface CorrectCollectorPayload {
  systeem?: string;
  collector_m2?: number;
  kringen?: Array<{ kringId: number; x_system?: string; x_legpatroon?: number }>;
}

/**
 * Correct a collector's systeem, m², and per-kring LP/systeem before billing.
 * 409 ALREADY_ON_PO when the collector is already billed (locked).
 */
/**
 * Bill a meerwerk onto the subcontractor's rolling PO.
 * unitPrice is only for werkuren/overig — km rejects it (code KM_PRICE_FIXED).
 * Throws ApiError with:
 *   - code === 'MEERWERK_ALREADY_ON_PO' for 409
 *   - code === 'OWN_WORKER_NO_PO' for 400 (own employee, use dismiss instead)
 */
export async function approveMeerwerkToPo(id: number, opts?: { unitPrice?: number }): Promise<AddToPoResult> {
  return api<AddToPoResult>(`/api/photos/meerwerk/${id}/add-to-po`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts ?? {}),
  });
}

/**
 * Dismiss a pending meerwerk (will not be billed).
 * Throws ApiError code === 'MEERWERK_NOT_PENDING' if it is not in pending state.
 */
export async function dismissMeerwerk(id: number): Promise<{ id: number; dismissed: boolean }> {
  return api<{ id: number; dismissed: boolean }>(`/api/photos/meerwerk/${id}/dismiss`, {
    method: 'PATCH',
  });
}

export async function correctCollector(
  collectorId: number,
  payload: CorrectCollectorPayload,
): Promise<void> {
  await api(`/api/photos/collector/${collectorId}/correction`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function uploadMateriaallijst(
  projectId: number,
  taskId: number,
  file: File,
): Promise<{ pickingId: number; pickingName: string; lineCount: number }> {
  const formData = new FormData();
  // Fields MUST come before file for @fastify/multipart
  formData.append('taskId', String(taskId));
  formData.append('file', file);
  return api<{ pickingId: number; pickingName: string; lineCount: number }>(
    `/api/projects/${projectId}/materiaallijst`,
    { method: 'POST', body: formData },
  );
}

export async function deleteMateriaallijst(
  projectId: number,
  pickingId: number,
): Promise<{ deleted: { picking: number; moves: number; lines: number } }> {
  return api<{ deleted: { picking: number; moves: number; lines: number } }>(
    `/api/projects/${projectId}/materiaallijst/${pickingId}`,
    { method: 'DELETE' },
  );
}

export async function searchDocuments(projectId: number): Promise<{ found: boolean; path: string | null; items: SharePointItem[]; breadcrumb: string[]; truncated?: boolean }> {
  return api<{ found: boolean; path: string | null; items: SharePointItem[]; breadcrumb: string[]; truncated?: boolean }>(`/api/projects/${projectId}/documents/search`);
}

export async function listDocuments(projectId: number, path: string): Promise<{ items: SharePointItem[]; breadcrumb: string[]; truncated?: boolean }> {
  return api<{ items: SharePointItem[]; breadcrumb: string[]; truncated?: boolean }>(`/api/projects/${projectId}/documents/list?path=${encodeURIComponent(path)}`);
}

export async function saveDocument(projectId: number, path: string, filename: string): Promise<{ url: string; key: string }> {
  return api<{ url: string; key: string }>(`/api/projects/${projectId}/documents/save`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path, filename }),
  });
}

/**
 * Upload an image to a task description as an Odoo ir.attachment.
 * Returns the public Odoo src URL and attachment id.
 */
export async function uploadTaskDescriptionImage(
  projectId: number,
  taskId: number,
  file: File,
): Promise<{ src: string; attachmentId: number }> {
  const formData = new FormData();
  // Only field is the file itself — no ordering concern (no other fields).
  formData.append('file', file);
  return api<{ src: string; attachmentId: number }>(
    `/api/projects/${projectId}/tasks/${taskId}/description-image`,
    { method: 'POST', body: formData },
  );
}

/**
 * Save the task description as sanitized HTML.
 * The server returns the server-sanitized HTML so the client can cache the
 * exact stored value (avoids stale re-render after switch-away/back).
 */
export async function updateTaskDescription(
  projectId: number,
  taskId: number,
  description: string,
): Promise<{ description: string }> {
  return api<{ description: string }>(`/api/projects/${projectId}/tasks/${taskId}/description`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ description }),
  });
}

/** Persist free-text dashboard notes on project.project (Fase 4). Plain text. */
export async function updateProjectNotes(projectId: number, notes: string) {
  return api(`/api/projects/${projectId}/notes`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ notes }),
  });
}

export type PlanningStage = 'In uitvoering' | 'Mag worden gevorderd' | 'Einde werken Volledig Project';

export async function updateTaskPlanningStage(
  projectId: number,
  taskId: number,
  stage: PlanningStage,
): Promise<{ id: number; x_studio_planningstage: string }> {
  return api<{ id: number; x_studio_planningstage: string }>(
    `/api/projects/${projectId}/tasks/${taskId}/planning-stage`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stage }),
    },
  );
}

export async function listProjectPlans(projectId: number) {
  return api<{ plans: PhotoItem[] }>(`/api/projects/${projectId}/plans`);
}

export async function uploadProjectPlan(projectId: number, file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return api<{ key: string; url: string; filename: string }>(`/api/projects/${projectId}/plans`, {
    method: 'POST',
    body: formData,
  });
}

export async function deleteProjectPlan(projectId: number, key: string) {
  return api(`/api/projects/${projectId}/plans?key=${encodeURIComponent(key)}`, { method: 'DELETE' });
}

export async function saveDocumentToPlans(projectId: number, path: string, filename: string): Promise<{ key: string }> {
  return api<{ key: string }>(`/api/projects/${projectId}/documents/save-to-plans`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path, filename }),
  });
}

export async function toggleOpgelijst(taskId: number, opgelijst: boolean): Promise<{ updated: number }> {
  return api<{ updated: number }>(`/api/tasks/${taskId}/opgelijst`, {
    method: 'PATCH',
    body: JSON.stringify({ opgelijst }),
  });
}

export interface CreateCollectorPayload {
  project_id: number;
  name: string;
  verdieping: string;
  blok: string;
  collector_m2: number;
  systeem: CadSysteemEnum;
  materiaallijst_id?: number;
  kringen: Array<{
    x_kringnummer: string;
    x_system: CadSysteemEnum;
    x_legpatroon: string;
    x_length: number;
  }>;
}

export async function createCollector(data: CreateCollectorPayload): Promise<{ collector_id: number; kringen_created: number }> {
  return api<{ collector_id: number; kringen_created: number }>('/api/collectors', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export interface UpdateCollectorPayload {
  name?: string;
  verdieping?: string;
  blok?: string;
  collector_m2?: number;
  systeem?: string;
  materiaallijst_id?: number | null;
}

export async function updateCollector(taskId: number, data: UpdateCollectorPayload): Promise<{ taskId: number; updated: string[] }> {
  return api<{ taskId: number; updated: string[] }>(`/api/collectors/${taskId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function fetchKringen(taskId: number): Promise<{ kringen: Kring[] }> {
  return api<{ kringen: Kring[] }>(`/api/collectors/${taskId}/kringen`);
}

export async function createKring(taskId: number, data: { x_kringnummer: number; x_system: string; x_legpatroon: number; x_length: number }): Promise<{ id: number }> {
  return api<{ id: number }>(`/api/collectors/${taskId}/kringen`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updateKring(taskId: number, kringId: number, data: { x_kringnummer?: number; x_system?: string; x_legpatroon?: number; x_length?: number }): Promise<void> {
  await api(`/api/collectors/${taskId}/kringen/${kringId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteKring(taskId: number, kringId: number): Promise<void> {
  await api(`/api/collectors/${taskId}/kringen/${kringId}`, { method: 'DELETE' });
}

export interface SplitCollectorPayload {
  /** Kringen that MOVE to deel 2; the rest stay on deel 1. */
  kring_ids: number[];
  /** Name for deel 1 (the existing card). Omit to keep the current name. */
  source_name?: string;
  /** Name for deel 2. Omit to let the API derive it. */
  new_name?: string;
  /** Optional installateur for deel 2. Empty = unclaimed, first uploader takes it. */
  installateur_id?: number | null;
}

export interface SplitCollectorResult {
  collector_id: number;
  source_name: string;
  new_name: string;
  m2_deel1: number;
  m2_deel2: number;
  kringen_moved: number;
  photos_moved: number;
}

/**
 * Split one collector into two cards. The API owns the m² division (kring-length
 * share) and every refusal — the modal's preview is advisory, the server's numbers
 * are the ones that land.
 */
export async function splitCollector(
  taskId: number,
  data: SplitCollectorPayload,
): Promise<SplitCollectorResult> {
  return api<SplitCollectorResult>(`/api/collectors/${taskId}/split`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteCollector(taskId: number): Promise<void> {
  await api(`/api/collectors/${taskId}`, { method: 'DELETE' });
}

export async function syncCollectorFotoStatus(
  collectorId: number,
  meta: { projectName: string; blok: string; verdieping: string; collectorName: string },
): Promise<{ fotoStatus: string; photoCount: number }> {
  const params = new URLSearchParams(meta);
  return api<{ fotoStatus: string; photoCount: number }>(`/api/photos/collector/${collectorId}/sync-status?${params}`, {
    method: 'PATCH',
  });
}

export async function movePhotoToDrukattest(
  collectorId: number,
  key: string,
  blokName: string,
): Promise<{ newKey: string }> {
  return api<{ newKey: string }>(`/api/photos/collector/${collectorId}/move-to-drukattest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, blokName }),
  });
}

export async function generateProjectPdf(projectId: number, collectorIds?: number[]): Promise<void> {
  const baseUrl = import.meta.env.VITE_API_URL || '';
  const token = useAuthStore.getState().token;
  const headers: Record<string, string> = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Dev persona headers (same logic as client.ts doFetch)
  const isRealAuth = import.meta.env.VITE_SKIP_AUTH !== 'true';
  if (!isRealAuth) {
    const user = useAuthStore.getState().user;
    if (user) {
      headers['X-Dev-Persona'] = JSON.stringify({
        name: user.name,
        email: user.email,
        roles: user.roles,
        odooId: user.odooId,
      });
    }
  } else {
    const override = localStorage.getItem('dev-role-override');
    if (override) {
      headers['X-Dev-Persona'] = override;
    }
  }

  const query = collectorIds && collectorIds.length > 0
    ? `?collector_ids=${collectorIds.join(',')}`
    : '';
  const resp = await fetch(`${baseUrl}/api/photos/project/${projectId}/pdf${query}`, { headers });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ message: 'PDF generatie mislukt' }));
    throw new Error(err.message || 'PDF generatie mislukt');
  }

  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  // Extract filename from Content-Disposition header, fallback to default
  const disposition = resp.headers.get('Content-Disposition');
  const filenameMatch = disposition?.match(/filename="(.+)"/);
  a.download = filenameMatch?.[1] || `project_${projectId}_fotos.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export interface FotoRapportShareLink {
  url: string;
  expiresAt: string | null;
  filename: string;
  /** Same photos as loose files; null when the ZIP could not be made. */
  zipUrl: string | null;
}

export async function createFotoRapportShareLink(
  projectId: number,
  collectorIds?: number[],
): Promise<FotoRapportShareLink> {
  return api<FotoRapportShareLink>(`/api/photos/project/${projectId}/pdf/share`, {
    method: 'POST',
    body: collectorIds && collectorIds.length > 0
      ? JSON.stringify({ collector_ids: collectorIds })
      : undefined,
  });
}

export interface FotoRapportShareLinkSummary {
  token: string;
  url: string;
  filename: string;
  s3Key: string;
  createdAt: string;
  expiresAt: string | null;
}

export async function listFotoRapportShareLinks(projectId: number): Promise<FotoRapportShareLinkSummary[]> {
  const res = await api<{ links: FotoRapportShareLinkSummary[] }>(
    `/api/photos/project/${projectId}/share-links`,
  );
  return res.links;
}

export async function deleteFotoRapportShareLink(token: string): Promise<void> {
  await api(`/api/photos/project/share-links/${encodeURIComponent(token)}`, {
    method: 'DELETE',
  });
}